import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { Api } from "../api/api";
import { AvailabilityUtils } from './utils/AvailabilityUtils';
import { CartSliceUtils } from './utils/CartSliceUtils';
import { consumeSearchTracking } from './filtersSlice';
import { ProductsUtils } from './utils/ProductsUtils';

const PRODUCTS_PER_PAGE = 25;

const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';
const STATUS_SUCCEEDED = 'succeeded';
const STATUS_FAILED = 'failed';

const initialState = {
    status: STATUS_IDLE,
    error: null,

    availability_status: STATUS_SUCCEEDED,
    availability_error: null,
    products_availability_trigger: new Date().getTime(),

    generation: 0,
    countReady: false,
    countRequestId: null,
    productRequestId: null,
    availabilityRequestId: null,
    productsCount: 0,
    products: null,

    specifications: null,
};

export const getProducts = createAsyncThunk(
    'shop/getProducts',
    async ({ page }, { getState }) => {
        const state = getState();
        const category = state.filters.category;
        const location = state.filters.location;

        const isSearch = state.filters.search;
        const searchCriteria = state.filters.submittedSearchCriteria;

        const specificationsFilters = state.filters.specificationsFilters;

        const recurring = state.filters.recurring;
        const startDate = recurring ? state.filters.startDateRecurring : state.filters.startDate;
        const endDate = recurring ? state.filters.endDateRecurring : state.filters.endDate;

        const cartArticlesTenantId = CartSliceUtils.getCartItemsTenantId(state);

        const response = await Api.getProducts(category, startDate, endDate, isSearch, searchCriteria, specificationsFilters, location, cartArticlesTenantId, page, PRODUCTS_PER_PAGE);
        return { response, generation: state.products.generation };
    }
)

export const getProductsCount = createAsyncThunk(
    'shop/getProductsCount',
    async (_params, { getState, dispatch }) => {
        const state = getState();
        const category = state.filters.category;
        const location = state.filters.location;

        const isSearch = state.filters.search;
        const searchCriteria = state.filters.submittedSearchCriteria;

        const specificationsFilters = state.filters.specificationsFilters;

        const recurring = state.filters.recurring;
        const startDate = recurring ? state.filters.startDateRecurring : state.filters.startDate;
        const endDate = recurring ? state.filters.endDateRecurring : state.filters.endDate;

        const trackSearchTerm = state.filters.trackSearchTerm;
        dispatch(consumeSearchTracking());
        const response = await Api.getProductsCount(category, startDate, endDate, isSearch, searchCriteria, specificationsFilters, location, trackSearchTerm);
        if (!Number.isFinite(Number(response.articles_count)) || !Array.isArray(response.attributes)) {
            throw new Error('Invalid product count response');
        }
        return response;
    }
)

export const checkAvailabilityForProducts = createAsyncThunk(
    'shop/checkAvailabilityForProducts',
    async (_noParams, { getState }) => {
        const state = getState();
        const location = state.filters.location;

        const products = state.products.products;
        const cartArticlesTenantId = CartSliceUtils.getCartItemsTenantId(state);

        const dataForAvailability = AvailabilityUtils.prepareAvailabilityDataForProducts(products, state, {}, false/* do not read availability for each day */);
        const response = await Api.checkAvailabilityForProducts(dataForAvailability, cartArticlesTenantId, location);
        
        return { response, generation: state.products.generation };
    }
)

export const slice = createSlice({
    name: 'products',
    initialState,

    reducers: {
        setStatus: (state, action) => {
            state.status = action.payload;
        },
        // Called to trigger availability check at your request - not used at this point
        triggerProductsAvailability: (state, action) => {
            state.products_availability_trigger = new Date().getTime();
        },
        clearProducts: (state) => {
            state.products = null;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(getProducts.pending, (state, action) => {
                state.productRequestId = action.meta.requestId;
                state.status = STATUS_LOADING;
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                if (state.productRequestId !== action.meta.requestId || state.generation !== action.payload.generation) return;
                state.status = STATUS_SUCCEEDED;

                state.products = {...state.products, ...action.payload.response.articles};
            })
            .addCase(getProducts.rejected, (state, action) => {
                if (state.productRequestId !== action.meta.requestId) return;
                state.status = STATUS_FAILED;
                state.error = action.error.message;
            })

            .addCase(getProductsCount.pending, (state, action) => {
                state.generation += 1;
                state.countReady = false;
                state.countRequestId = action.meta.requestId;
                state.productRequestId = null;
                state.availabilityRequestId = null;
                state.status = STATUS_LOADING;
                state.productsCount = 0;
                state.products = null;
            })
            .addCase(getProductsCount.fulfilled, (state, action) => {
                if (state.countRequestId !== action.meta.requestId) return;
                state.countReady = true;
                state.status = STATUS_SUCCEEDED;

                state.products = null;
                state.productsCount = parseInt(action.payload.articles_count);
                state.specifications = ProductsUtils.prepareAttributes(action.payload.attributes);
            })
            .addCase(getProductsCount.rejected, (state, action) => {
                if (state.countRequestId !== action.meta.requestId) return;
                state.status = STATUS_FAILED;
                state.error = action.error.message;
            })

            .addCase(checkAvailabilityForProducts.pending, (state, action) => {
                state.availabilityRequestId = action.meta.requestId;
                state.availability_status = STATUS_LOADING;
            })
            .addCase(checkAvailabilityForProducts.fulfilled, (state, action) => {
                if (state.availabilityRequestId !== action.meta.requestId || state.generation !== action.payload.generation) return;
                state.availability_status = STATUS_SUCCEEDED;

                if(state.products) {
                    for (const [id, product] of Object.entries(state.products)) {
                        const availability = action.payload.response[product.product_id];
                        if (availability) state.products[id] = {...product, available: availability.available};
                    }
                }
            })
            .addCase(checkAvailabilityForProducts.rejected, (state, action) => {
                if (state.availabilityRequestId !== action.meta.requestId) return;
                state.availability_status = STATUS_FAILED;
                state.availability_error = action.error.message;
            })
            .addMatcher(action => [
                'filters/submitGlobalSearch', 'filters/clearSearch', 'filters/setCategory',
                'filters/addSpecificationsFilter', 'filters/setLocation'
            ].includes(action.type), (state) => {
                state.generation += 1;
                state.countReady = false;
                state.countRequestId = null;
                state.productRequestId = null;
                state.availabilityRequestId = null;
                state.products = null;
                state.productsCount = 0;
                state.status = STATUS_IDLE;
            })
    }
});

export const { setStatus, triggerProductsAvailability, clearProducts } = slice.actions;

export const selectIsLoading = state => state.products.status === STATUS_LOADING;
export const selectStatusApiHasFailed = state => state.products.status === STATUS_FAILED;
export const selectIsLoadingAvailability = state => state.products.availability_status === STATUS_LOADING;
export const selectTriggerProductsAvailability = state => state.products.products_availability_trigger;

export const selectProductsCount = state => state.products.productsCount;
export const selectProducts = state => state.products.products;

export const selectSpecifications = state => state.products.specifications;

export default slice.reducer;